import { useCallback, useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PublicPageCard } from '@/components/public-rsvp/PublicPageCard';
import { PublicRSVPForm, type PublicRSVPFormValues } from '@/components/public-rsvp/PublicRSVPForm';
import { fetchPickupLocations } from '@/lib/api/pickupLocations';
import {
  checkPublicRSVPEligibility,
  fetchPublicRSVPEvent,
  lookupPublicRSVP,
  submitPublicRSVP,
} from '@/lib/api/publicRsvps';
import { fromISODate, getWeekRange, toISODate } from '@/lib/utils/dateHelpers';
import { useAuthStore } from '@/stores/authStore';
import type { Menu, PickupLocation, PublicRSVPEligibilityResponse } from '@/types/api';
import bismillahSrc from '@/images/bismillah.png';

type PublicStep = 'its' | 'form' | 'confirmation';

function isActiveMemberResponse(
  response: PublicRSVPEligibilityResponse | { editPath?: string }
): response is Extract<PublicRSVPEligibilityResponse, { status: 'ACTIVE_MEMBER_EXISTS' }> {
  return (response as PublicRSVPEligibilityResponse).status === 'ACTIVE_MEMBER_EXISTS';
}

function normalizeItsId(value: string) {
  return value.replace(/\D/g, '');
}

function buildMemberRsvpPath(menuDate: string): string {
  const weekStart = toISODate(getWeekRange(fromISODate(menuDate)).start);
  return `/rsvp?weekStart=${encodeURIComponent(weekStart)}&highlightDate=${encodeURIComponent(menuDate)}`;
}

export function PublicRSVP() {
  const { slug = '' } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, refreshUser } = useAuthStore();

  const [menu, setMenu] = useState<Menu | null>(null);
  const [pickupLocations, setPickupLocations] = useState<PickupLocation[]>([]);
  const [canRsvp, setCanRsvp] = useState(false);
  const [unavailableReason, setUnavailableReason] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionChecked, setSessionChecked] = useState(false);
  const [step, setStep] = useState<PublicStep>('its');
  const [itsId, setItsId] = useState('');
  const [activeMemberTarget, setActiveMemberTarget] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmationPath, setConfirmationPath] = useState<string | null>(null);
  const [lookupItsId, setLookupItsId] = useState('');
  const [lookupPhone, setLookupPhone] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);

  const publicLink = useMemo(() => {
    if (!confirmationPath) return '';
    return `${window.location.origin}${confirmationPath}`;
  }, [confirmationPath]);
  const memberRsvpPath = useMemo(
    () => (menu?.date ? buildMemberRsvpPath(menu.date) : '/rsvp'),
    [menu?.date]
  );
  const activeMember = activeMemberTarget !== null;
  const shouldAutoRedirectToMemberFlow = Boolean(
    sessionChecked &&
    canRsvp &&
    menu?.date &&
    isAuthenticated &&
    user?.status === 'ACTIVE'
  );

  const loadEvent = useCallback(async () => {
    if (!slug) return;

    try {
      setLoading(true);
      const [eventResponse, locations] = await Promise.all([
        fetchPublicRSVPEvent(slug),
        fetchPickupLocations(true),
      ]);
      setMenu(eventResponse.menu);
      setCanRsvp(eventResponse.canRsvp);
      setUnavailableReason(eventResponse.unavailableReason);
      setPickupLocations(locations);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load this RSVP link');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    loadEvent();
  }, [loadEvent]);

  useEffect(() => {
    let cancelled = false;

    const checkSession = async () => {
      if (isAuthenticated && user) {
        if (!cancelled) setSessionChecked(true);
        return;
      }

      try {
        await refreshUser();
      } finally {
        if (!cancelled) setSessionChecked(true);
      }
    };

    checkSession();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, refreshUser, user]);

  useEffect(() => {
    if (shouldAutoRedirectToMemberFlow) {
      navigate(memberRsvpPath, { replace: true });
    }
  }, [memberRsvpPath, navigate, shouldAutoRedirectToMemberFlow]);

  const handleItsSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setActiveMemberTarget(null);

    if (!itsId) {
      setError('ITS ID is required');
      return;
    }

    try {
      setSubmitting(true);
      const response = await checkPublicRSVPEligibility(slug, itsId);
      if (isActiveMemberResponse(response)) {
        setActiveMemberTarget(response.rsvpPath || memberRsvpPath);
        return;
      }
      setStep('form');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to check RSVP eligibility');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePublicSubmit = async (values: PublicRSVPFormValues) => {
    setError(null);

    try {
      setSubmitting(true);
      const response = await submitPublicRSVP(slug, {
        itsId,
        ...values,
      });
      setConfirmationPath(response.editPath);
      setStep('confirmation');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to submit RSVP';
      if (message === 'ACTIVE_MEMBER_EXISTS') {
        setActiveMemberTarget(memberRsvpPath);
        setStep('its');
      } else if (message === 'PUBLIC_RSVP_EXISTS') {
        setError('An RSVP already exists for this ITS ID. Use the lookup form below with the original phone number to manage it.');
      } else {
        setError(message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleLookup = async (event: FormEvent) => {
    event.preventDefault();
    setLookupError(null);
    setActiveMemberTarget(null);

    if (!lookupItsId || !lookupPhone.trim()) {
      setLookupError('ITS ID and phone number are required');
      return;
    }

    try {
      setLookupLoading(true);
      const response = await lookupPublicRSVP(slug, {
        itsId: lookupItsId,
        phone: lookupPhone.trim(),
      });

      if (isActiveMemberResponse(response)) {
        setActiveMemberTarget(response.rsvpPath || memberRsvpPath);
        return;
      }

      if ('editPath' in response) {
        navigate(response.editPath);
      }
    } catch (err) {
      setLookupError(err instanceof Error ? err.message : 'Unable to find that RSVP');
    } finally {
      setLookupLoading(false);
    }
  };

  const goToLogin = () => {
    navigate('/login', {
      state: { from: activeMemberTarget || memberRsvpPath },
    });
  };

  if (loading || !sessionChecked || shouldAutoRedirectToMemberFlow) {
    return (
      <PublicShell>
        <Card className="p-8 text-center">
          {shouldAutoRedirectToMemberFlow ? (
            <div className="space-y-2">
              <p className="font-semibold">You&apos;re already signed in.</p>
              <p className="text-sm text-muted-foreground">
                Taking you to the member RSVP page for this menu.
              </p>
            </div>
          ) : (
            <div className="mx-auto h-10 w-10 animate-spin rounded-full border-b-2 border-primary" />
          )}
        </Card>
      </PublicShell>
    );
  }

  if (!menu) {
    return (
      <PublicShell>
        <Card className="p-8 text-center">
          <p className="font-semibold">RSVP link not found</p>
          <p className="text-sm text-muted-foreground">Please check the link and try again.</p>
        </Card>
      </PublicShell>
    );
  }

  return (
    <PublicShell>
      <div className="space-y-4">
        <PublicPageCard menu={menu} />

        {!canRsvp && (
          <Card className="border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            {unavailableReason === 'RSVP_CUTOFF_PASSED'
              ? 'RSVP cutoff has passed for this menu.'
              : unavailableReason || 'Public RSVP is not available for this menu.'}
          </Card>
        )}

        {activeMember && (
          <Card className="border-primary/30 bg-primary/5 p-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="font-semibold">This ITS ID has an active FMB account.</p>
                <p className="text-sm text-muted-foreground">
                  Please sign in and RSVP through the member flow so your regular preferences are used.
                </p>
              </div>
              <Button type="button" onClick={goToLogin}>
                Sign in
              </Button>
            </div>
          </Card>
        )}

        {error && (
          <Card className="border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
            {error}
          </Card>
        )}

        {canRsvp && step === 'its' && (
          <Card>
            <CardContent className="pt-6">
              <form onSubmit={handleItsSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="its-id">ITS ID</Label>
                  <Input
                    id="its-id"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={itsId}
                    onChange={(event) => setItsId(normalizeItsId(event.target.value))}
                    disabled={submitting}
                    required
                  />
                </div>
                <Button type="submit" className="w-full" disabled={submitting}>
                  {submitting ? 'Checking...' : 'Continue'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {canRsvp && step === 'form' && (
          <Card>
            <CardContent className="pt-6">
              <div className="mb-4 rounded-md bg-muted/50 p-3 text-sm">
                ITS ID: <span className="font-medium">{itsId}</span>
              </div>
              <PublicRSVPForm
                menu={menu}
                pickupLocations={pickupLocations}
                submitting={submitting}
                submitLabel="Submit RSVP"
                onSubmit={handlePublicSubmit}
              />
            </CardContent>
          </Card>
        )}

        {step === 'confirmation' && confirmationPath && (
          <Card>
            <CardContent className="space-y-4 pt-6">
              <div>
                <h2 className="text-xl font-semibold">RSVP confirmed</h2>
                <p className="text-sm text-muted-foreground">
                  Keep this secure link to edit or cancel before cutoff.
                </p>
              </div>
              <Input value={publicLink} readOnly />
              <div className="flex flex-col gap-2 sm:flex-row">
                <Button type="button" className="flex-1" onClick={() => navigate(confirmationPath)}>
                  Manage RSVP
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => navigator.clipboard?.writeText(publicLink)}
                >
                  Copy link
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {canRsvp && step !== 'confirmation' && (
          <Card>
            <CardContent className="pt-6">
              <form onSubmit={handleLookup} className="space-y-4">
                <div>
                  <p className="font-medium">Already submitted?</p>
                  <p className="text-sm text-muted-foreground">
                    Look up your RSVP using the same ITS ID and phone number.
                  </p>
                </div>
                {lookupError && (
                  <p className="text-sm text-destructive">{lookupError}</p>
                )}
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="lookup-its-id">ITS ID</Label>
                    <Input
                      id="lookup-its-id"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={lookupItsId}
                      onChange={(event) => setLookupItsId(normalizeItsId(event.target.value))}
                      disabled={lookupLoading}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lookup-phone">Phone</Label>
                    <Input
                      id="lookup-phone"
                      type="tel"
                      value={lookupPhone}
                      onChange={(event) => setLookupPhone(event.target.value)}
                      disabled={lookupLoading}
                    />
                  </div>
                </div>
                <Button type="submit" variant="outline" className="w-full" disabled={lookupLoading}>
                  {lookupLoading ? 'Looking up...' : 'Find my RSVP'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        <p className="text-center text-xs text-muted-foreground">
          Active FMB members should <Link to="/login" className="text-primary underline">sign in</Link> to RSVP.
        </p>
      </div>
    </PublicShell>
  );
}

export function PublicShell({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center warm-gradient-bg p-4">
      <div className="w-full max-w-2xl">
        <div className="mb-6 flex flex-col items-center">
          <img
            src={bismillahSrc}
            alt="Bismillah"
            className="mb-5 h-auto w-64 drop-shadow-sm dark-logo-filter sm:w-72"
          />
          <h1 className="text-center text-xl font-semibold tracking-tight text-stone-800 dark:text-stone-200 sm:text-2xl">
            Faiz-ul-Mawaid al-Burhaniyah
          </h1>
          <p className="mt-1 text-center text-sm uppercase tracking-widest text-stone-500 dark:text-stone-400">
            San Jose
          </p>
        </div>
        {children}
      </div>
    </div>
  );
}
